import { createLogger } from '../utils/logger';

/**
 * Creates a base API client with common HTTP operations.
 *
 * Mirrors the factory used by sibling MFEs (user-management, reports, ...) so
 * shell-side service modules look and behave the same way as those in the
 * remotes.
 *
 * @param {string} baseUrl - Base URL for the resource collection.
 * @param {string} resourceName - Used in error messages and logs.
 */
export const createAPIClient = (baseUrl, resourceName) => {
    const logger = createLogger('Shell::createAPIClient');

    const buildUrl = (path = '', params = {}) => {
        const url = new URL(path ? `${baseUrl}/${path}` : baseUrl);
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                url.searchParams.append(key, value);
            }
        });
        return url;
    };

    const makeRequest = async (url, options = {}) => {
        try {
            const response = await fetch(url, options);
            logger.info(`${options.method || 'GET'} ${url} - Status: ${response.status}`);

            if (!response.ok) {
                const errorText = await response.text();
                logger.error('Error response:', errorText.substring(0, 200) + '...');
                throw new Error(`Failed to ${options.method || 'GET'} ${resourceName}: ${response.status}`);
            }

            return response;
        } catch (error) {
            logger.error(`Error in ${options.method || 'GET'} request:`, error);
            throw error;
        }
    };

    const defaultHeaders = (token) => ({
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    });

    return {
        get: async (token, params = {}, path = '') => {
            const url = buildUrl(path, params);
            await makeRequest(url, {
                method: 'GET',
                headers: defaultHeaders(token)
            });
            return { success: true, messageId: params.correlation_id };
        },

        create: async (token, data, params = {}, path = '') => {
            const url = buildUrl(path, params);
            await makeRequest(url, {
                method: 'POST',
                headers: defaultHeaders(token),
                body: JSON.stringify(data)
            });
            return { success: true, messageId: params.correlation_id };
        },

        update: async (token, data, params = {}, path = '') => {
            const url = buildUrl(path, params);
            await makeRequest(url, {
                method: 'PUT',
                headers: defaultHeaders(token),
                body: JSON.stringify(data)
            });
            return { success: true, messageId: params.correlation_id };
        },

        delete: async (token, params = {}, path = '') => {
            const url = buildUrl(path, params);
            await makeRequest(url, {
                method: 'DELETE',
                headers: defaultHeaders(token)
            });
            return { success: true, messageId: params.correlation_id };
        },

        _buildUrl: buildUrl,
        _makeRequest: makeRequest,
        _logger: logger
    };
};